import { useMemo, useEffect, useState } from 'react';
import type { CanvasData } from '../utils/canvas-utils';

type UseCanvasAlignmentParams = {
  computedTextAlign?: string;
  rootRef: React.RefObject<HTMLElement | null>;
  textRef: React.RefObject<HTMLElement | null>;
  canvasData: CanvasData | undefined;
};

/**
 * Custom hook to calculate canvas alignment offset based on text alignment.
 * Aligns the actual text content (not canvas edges) to the root element boundaries.
 */
export const useCanvasAlignment = ({
  computedTextAlign,
  rootRef,
  textRef,
  canvasData,
}: UseCanvasAlignmentParams): string => {
  const [dimensions, setDimensions] = useState<{
    rootWidth: number;
    textWidth: number;
  } | null>(null);

  useEffect(() => {
    if (!rootRef.current || !textRef.current || !canvasData) {
      setDimensions(null);
      return;
    }

    const updateDimensions = () => {
      const rootRect = rootRef.current?.getBoundingClientRect();
      const textRect = textRef.current?.getBoundingClientRect();

      if (rootRect && textRect) {
        setDimensions({
          rootWidth: rootRect.width,
          textWidth: textRect.width,
        });
      }
    };

    updateDimensions();

    // Observe resize changes
    const resizeObserver = new ResizeObserver(updateDimensions);
    if (rootRef.current) {
      resizeObserver.observe(rootRef.current);
    }
    if (textRef.current) {
      resizeObserver.observe(textRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [rootRef, textRef, canvasData]);

  const transform = useMemo(() => {
    if (!dimensions || !canvasData) {
      return 'translateX(0)';
    }

    // Determine the alignment value from computed CSS
    // Default to 'center' if not specified
    let alignment: 'left' | 'center' | 'right' = 'center';

    if (computedTextAlign) {
      // Normalize CSS text-align values to our alignment type
      if (
        computedTextAlign === 'left' ||
        computedTextAlign === 'start' ||
        computedTextAlign === 'inherit'
      ) {
        alignment = 'left';
      } else if (computedTextAlign === 'right' || computedTextAlign === 'end') {
        alignment = 'right';
      } else if (computedTextAlign === 'center') {
        alignment = 'center';
      }
    }

    const { rootWidth, textWidth } = dimensions;

    // Calculate offset to align text content to root element edges
    let offsetX = 0;

    if (alignment === 'left') {
      // Move canvas left so text aligns with left edge
      offsetX = -(rootWidth - textWidth) / 2;
    } else if (alignment === 'right') {
      // Move canvas right so text aligns with right edge
      offsetX = (rootWidth - textWidth) / 2;
    }
    // For 'center', offsetX remains 0

    // Always include px unit for consistency
    return `translateX(${offsetX}px)`;
  }, [computedTextAlign, dimensions, canvasData]);

  return transform;
};
